import { cloneElement, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { studioBOn } from '@/lib/studioB/flag';

export const ICON_TIP_DELAY = 300;

type Side = 'top' | 'right' | 'bottom' | 'left';

/**
 * Studio B (beta only): an icon-only control's words, on hover and on keyboard focus, after one short delay, the same
 * everywhere. It replaces the browser's slow `title` and names the control for screen readers when it has no label.
 * Touch has no hover: there the words come from the control's label (VoiceOver) and the rail's words. Off B the control
 * is exactly as it was. `off` keeps the control in the same tree but shows no tip (an open rail's rows, whose words show).
 */
export function IconTipB({ label, side = 'bottom', off = false, children }: { label: string; side?: Side; off?: boolean; children: JSX.Element }) {
  const [open, setOpen] = useState(false);
  if (!studioBOn()) return children;
  const named = (children.props as { 'aria-label'?: string })['aria-label'];
  const trigger: JSX.Element = cloneElement(children, { title: undefined, 'aria-label': named ?? label, 'data-icon-tip-trigger': '' });
  return (
    <TooltipPrimitive.Provider delayDuration={ICON_TIP_DELAY} skipDelayDuration={300}>
      <TooltipPrimitive.Root open={open && !off} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>
          {trigger}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          data-icon-tip
          className="z-[100] whitespace-nowrap rounded-[var(--b-radius-control)] bg-b-ink px-2 py-1 font-sans text-b-meta text-b-on-ink shadow-[var(--b-shadow-lift)] animate-in fade-in-0 motion-reduce:animate-none"
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
